import { EmailIcon, LockIcon } from '@chakra-ui/icons';
import {
    Box,
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    InputGroup,
    InputLeftElement,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    useToast,
} from '@chakra-ui/react';
import { colors, elevation } from 'constants/constants';
import { Field, Form, Formik, FormikErrors, FormikHelpers } from 'formik';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useContext, useState } from 'react';
import SessionContext, { ISessionContext } from 'session/SessionContext';

interface FormValues {
  email: string;
  password: string;
}

export default function LoginForm(): JSX.Element {
  const session = useContext<ISessionContext>(SessionContext);
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const toast = useToast();
  // const actionCodeSettings: firebase.auth.ActionCodeSettings = {
  //   url: `${window.location.origin}/finishsignup`,
  //   handleCodeInApp: true,
  // };
  async function handlePasswordlessLogin() {
    const actionCodeSettings = {
      url: `https://mitt-lager.firebaseapp.com`,
      handleCodeInApp: true,
    };

    await session.actions.sendSignInLinkToEmail(email, actionCodeSettings);
    toast({
      title: 'E-post skickad',
      description: 'Vi har skickat en länk till din e-postadress.',
      status: 'success',
      duration: 5000,
      isClosable: true,
    });
  }
  async function handleSubmitasync(
    values: FormValues,
    { setSubmitting, setErrors }: FormikHelpers<FormValues>
  ) {
    const updatedPassword = await session.actions.signIn(values);
    setSubmitting(false);
    if (updatedPassword.error) {
      const err: FormikErrors<FormValues> = {
        email: null,
        password: null,
      };
      err[updatedPassword.error.field] = updatedPassword.error.message;
      setErrors(err);
    } else {
      router.push('/mypages');
    }
  }

  return (
    <Box
      zIndex="10"
      p="3em"
      textAlign="center"
      bg={colors.white}
      borderRadius="1em"
      boxShadow={elevation['+80']}
      w="100%"
      maxW="25em">
      {/* <Logo
        fill={colors.primary[900]}
        width={150}
        style={{ margin: '0 auto' }}
      /> */}

      {!session.user ? (
        <>
          <Box>
            <Text
              style={{ fontWeight: 'bold', color: 'black' }}
              color={colors.neutrals[300]}>
              Logga in på Mina Sidor
            </Text>
            <Text>
              På Mina Sidor når du ditt avtal, dina betalningar samt din kod.
            </Text>
          </Box>
          <Formik
            initialValues={{ email: '', password: '' }}
            onSubmit={handleSubmitasync}>
            {(props) => {
              return (
                <Form>
                  <Field name="email">
                    {({ field, form }) => (
                      <FormControl
                        mb={3}
                        isInvalid={form.errors.email && form.touched.email}>
                        <FormLabel htmlFor="email">E-post</FormLabel>
                        <InputGroup>
                          <InputLeftElement>
                            <EmailIcon color={colors.neutrals[300]} />
                          </InputLeftElement>
                          <Input
                            type="email"
                            isRequired={true}
                            {...field}
                            id="email"
                            placeholder="E-post"
                            autoComplete="email"
                            aria-describedby="login-email-error"
                            aria-invalid={
                              form.errors.email && form.touched.email
                            }
                            bg="white"
                          />
                        </InputGroup>
                        <FormErrorMessage id="login-email-error">
                          {form.errors.email}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="password">
                    {({ field, form }) => (
                      <FormControl
                        isInvalid={
                          form.errors.password && form.touched.password
                        }>
                        <FormLabel htmlFor="password">Lösenord</FormLabel>
                        <InputGroup>
                          <InputLeftElement>
                            <LockIcon color={colors.neutrals[300]} />
                          </InputLeftElement>
                          <Input
                            isRequired={true}
                            type="password"
                            {...field}
                            id="password"
                            placeholder="Lösenord"
                            autoComplete="current-password"
                            aria-describedby="login-password-error"
                            aria-invalid={
                              form.errors.password && form.touched.password
                            }
                            bg="white"
                          />
                        </InputGroup>
                        <FormErrorMessage id="login-password-error">
                          {form.errors.password}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Button
                    mt={4}
                    w="100%"
                    variant="ml"
                    isLoading={props.isSubmitting}
                    type="submit">
                    Logga in
                  </Button>
                </Form>
              );
            }}
          </Formik>
          <Box mt="2" mb={2} color={colors.neutrals[300]}>
            <Link href="/forgotpassword">Glömt lösenordet?</Link>
          </Box>
          <Box>
            <Text mb={2}>
              Du kan också välja att logga in med en länk som skickas till din
              mail istället för lösenord.
            </Text>
            <Text style={{ fontWeight: 'bold' }}>
              Obs länken är endast giltig en gång.
            </Text>
            <Button
              mt={4}
              w="100%"
              variant="ml"
              onClick={() => setIsOpen(true)}>
              Logga in utan lösenord
            </Button>
          </Box>
        </>
      ) : (
        'Du är inloggad som ' + session.user.email
      )}
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Logga in med magic link</ModalHeader>

          <ModalBody>
            <Text mb={2}>
              Efter att ha angivit din e-postadress kommer ett mail skickas till
              dig. I mailet finns en unik inloggningslänk - klicka på den för
              att komma åt dina sidor.
            </Text>
            <Text style={{ fontWeight: 'bold' }}>
              Obs länken är endast giltig en gång.
            </Text>
            <FormControl>
              <FormLabel htmlFor="magic-link-email">E-post</FormLabel>
              <Input
                id="magic-link-email"
                mt={4}
                type="email"
                autoComplete="email"
                placeholder="E-post"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ml"
              mr={3}
              isDisabled={!email}
              onClick={async () => {
                handlePasswordlessLogin();
                setIsOpen(false);
                setEmail('');
              }}>
              Skicka länk
            </Button>

            <Button variant="ghost" onClick={() => setIsOpen(false)}>
              Avbryt
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
